import { supabase } from '../client'
import type { CloudHabit } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── HABITS (Cloud) ─────────────────────────────────────────── */

export async function getCloudHabits(userId: string, since?: Date): Promise<CloudHabit[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('habits')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('sort_order', { ascending: true })
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get habits error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get habits error', error)
    return []
  }
}

export async function getCloudHabitIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('habits')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)
    if (error) {
      logSupabaseError('Get habit IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get habit IDs error', error)
    return []
  }
}

export async function getCloudHabitsByIds(userId: string, ids: string[]): Promise<CloudHabit[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('habits')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get habits by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get habits by IDs error', error)
    return []
  }
}

export async function getDeletedCloudHabits(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('habits')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted habits error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted habits error', error)
    return []
  }
}

export async function createCloudHabit(
  userId: string,
  habit: Omit<CloudHabit, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudHabit | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('habits')
      .insert({ ...habit, user_id: userId })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create habit error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create habit error', error)
    return null
  }
}

export async function updateCloudHabit(
  habitId: string,
  updates: Partial<Omit<CloudHabit, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('habits')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', habitId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update habit error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update habit error', error)
    return null
  }
}

export async function deleteCloudHabit(habitId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('habits')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', habitId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete habit error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete habit error', error)
    return false
  }
}
